import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";

const STATUS_MAP = { pending: "pending", approved: "approved", rejected: "rejected" };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const act = async (path, id, msg) => {
    try {
      await api.post(`/admin/users/${id}/${path}`);
      toast.success(msg);
      load();
    } catch {
      toast.error("Action failed");
    }
  };

  const del = async (id, email) => {
    if (!window.confirm(`Delete ${email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const pendingCount = users.filter((u) => u.status === "pending").length;

  return (
    <AppLayout
      activeMenu="users"
      breadcrumb={[
        { label: "Sponsorship management system", to: "/" },
        { label: "Administration" },
        { label: "Users" },
      ]}
    >
      <h1>User approvals</h1>
      <p>
        Review sign-up requests and manage who can access this system.{" "}
        {pendingCount > 0 && (
          <span>
            <strong style={{ color: "#7a5a00" }}>{pendingCount}</strong> pending{" "}
            {pendingCount === 1 ? "request" : "requests"}.
          </span>
        )}
      </p>

      <div className="sms-panel">
        <div className="sms-panel-title">Registered users</div>
        <div className="sms-panel-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 24, color: "#666" }}>Loading…</div>
          ) : (
            <table className="sms-table" data-testid="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Signed up</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>{u.role === "admin" ? <strong>Admin</strong> : "User"}</td>
                    <td>
                      <span className={`sms-status-pill ${STATUS_MAP[u.status] || ""}`}>
                        {u.status}
                      </span>
                    </td>
                    <td>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {u.status !== "approved" && (
                        <button
                          type="button"
                          data-testid={`approve-btn-${u.id}`}
                          onClick={() => act("approve", u.id, "Approved")}
                          className="sms-btn small"
                        >
                          Approve
                        </button>
                      )}
                      {u.status === "approved" && u.role !== "admin" && (
                        <button
                          type="button"
                          data-testid={`reject-btn-${u.id}`}
                          onClick={() => act("reject", u.id, "Access revoked")}
                          className="sms-btn small secondary"
                        >
                          Revoke
                        </button>
                      )}
                      {u.status === "pending" && (
                        <>
                          {" "}
                          <button
                            type="button"
                            data-testid={`reject-btn-${u.id}`}
                            onClick={() => act("reject", u.id, "Rejected")}
                            className="sms-btn small secondary"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {u.role !== "admin" && (
                        <>
                          {" "}
                          <button
                            type="button"
                            data-testid={`delete-user-btn-${u.id}`}
                            onClick={() => del(u.id, u.email)}
                            className="sms-btn small secondary"
                            style={{ color: "#8f1414" }}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
